import type { IntlShape } from 'react-intl'
import qs from 'qs'
import type { TrendingDestination } from '../TrendingDestinations/constants'
import formatStaticAppText from 'lib/format/formatStaticAppText'
import { buildHotelUrl } from '../TrendingDestinations/utils'
import {
  isLocalDestinationOfferAllFilter,
  LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
} from './localDestinationOfferCarouselConstants'

export type TrendingDestinationWithId = TrendingDestination & { destinationId: string }

export type LocalDestinationOfferCarouselFilterOption = {
  value: string
  label: string
}

export type LocalDestinationOfferCarouselViewModel = {
  destinations: Array<TrendingDestinationWithId>
  destinationFilterOptions: Array<LocalDestinationOfferCarouselFilterOption>
  defaultDestinationId: string | undefined
}

export function getTrendingDestinationsWithIds(
  trendingDestinations: Array<TrendingDestination>,
): Array<TrendingDestinationWithId> {
  return trendingDestinations.filter(
    (destination): destination is TrendingDestinationWithId => !!destination.destinationId,
  )
}

export function mapDestinationsToFilterOptions(
  intl: IntlShape,
  destinations: Array<TrendingDestinationWithId>,
): Array<LocalDestinationOfferCarouselFilterOption> {
  return destinations.map(destination => ({
    value: destination.destinationId,
    label: formatStaticAppText(intl, destination.name),
  }))
}

export function formatLocalDestinationOfferAllLabel(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'aNwHZFGdWhRjMr7mbbDZ',
    defaultMessage: 'All',
    description: 'Filter chip label for showing hotel offers across all eligible trending destinations on the homepage local destination carousel',
  })
}

function buildAllDestinationFilterOption(intl: IntlShape): LocalDestinationOfferCarouselFilterOption {
  return {
    value: LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
    label: formatLocalDestinationOfferAllLabel(intl),
  }
}

export function buildLocalDestinationOfferCarouselViewModel(
  intl: IntlShape,
  trendingDestinations: Array<TrendingDestination>,
): LocalDestinationOfferCarouselViewModel {
  const destinations = getTrendingDestinationsWithIds(trendingDestinations)
  const destinationFilterOptions = destinations.length > 0
    ? [buildAllDestinationFilterOption(intl), ...mapDestinationsToFilterOptions(intl, destinations)]
    : []
  const defaultDestinationId = destinations.length > 0
    ? LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE
    : undefined

  return {
    destinations,
    destinationFilterOptions,
    defaultDestinationId,
  }
}

export function buildLocalDestinationViewAllUrl(
  destinations: Array<TrendingDestinationWithId>,
  selectedDestinationId?: string,
  allDestinationName?: string,
): string | undefined {
  if (isLocalDestinationOfferAllFilter(selectedDestinationId)) {
    if (destinations.length === 0 || !allDestinationName) {
      return undefined
    }

    // Use the same localised All chip label for the search-box destinationName.
    // Do not join destination names — commas break opaque destinationName parsing.
    return `/search?${qs.stringify({
      destinationName: allDestinationName,
      destinationId: destinations.map(destination => destination.destinationId).join(','),
      adults: 2,
      children: 'none',
      flexibleWithDate: true,
    })}`
  }

  const destination = destinations.find(item => item.destinationId === selectedDestinationId)
  if (!destination?.destinationId) {
    return undefined
  }
  return buildHotelUrl(destination.name, destination.destinationId)
}

export function isLocalDestinationOfferFilterSelectionValid(
  selectedFilter: string | undefined,
  destinations: Array<TrendingDestinationWithId>,
): boolean {
  if (!selectedFilter) {
    return false
  }

  if (isLocalDestinationOfferAllFilter(selectedFilter)) {
    return destinations.length > 0
  }

  return destinations.some(destination => destination.destinationId === selectedFilter)
}
